/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridSortModel, GridPaginationModel } from "@mui/x-data-grid";
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useAdminConfigCategoriesService } from '../services';
import { CampaignConfigCategoryView } from '../../campaignConfigBuilder/types/CampaignConfigCategoryView';
import { campaignConfigCategoryValidationSchema } from '../../../constants/validations';
import { ValidationError } from 'yup';

export const useAdminConfigCategoriesViewController = () => {
  const {
    isLoading,
    filters,
    configCategories,
    totalCount,
    error,
    getConfigCategories,
    createConfigCategory,
    updateConfigCategory,
    deleteConfigCategories,
    changeFilters,
  } = useAdminConfigCategoriesService();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingCategory, setEditingCategory] = useState<CampaignConfigCategoryView | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    description: '',
    icon: '',
    rank: 2,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reloadConfigCategories = useCallback(async () => {
    try {
      await getConfigCategories();
    } catch (error) {
      console.error('Error reloading config categories:', error);
    }
  }, [getConfigCategories]);

  // Fetch data when filters change or component mounts
  useEffect(() => {
    reloadConfigCategories();
  }, [filters]);

  const changeSearchFilter = useCallback((_: ChangeEvent<HTMLInputElement>, query: string) => {
    changeFilters({ query: query || undefined, offset: 0 });
  }, [changeFilters]);

  const changeSorting = useCallback((sort: GridSortModel) => {
    const sortString = sort.length > 0 ? `${sort[0].field}_${sort[0].sort}` : undefined;
    changeFilters({ sort: sortString, offset: 0 });
  }, [changeFilters]);

  const changePagination = useCallback((p: GridPaginationModel) => {
    changeFilters({ offset: p.page * p.pageSize, limit: p.pageSize });
  }, [changeFilters]);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      title: '',
      description: '',
      icon: '',
      rank: 2,
    });
    setFormErrors({});
  }, []);

  const openCreateDialog = useCallback(() => {
    resetForm();
    setDialogMode('create');
    setIsDialogOpen(true);
  }, [resetForm]);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
    setEditingCategory(null);
    resetForm();
  }, [resetForm]);

  const openEditDialog = useCallback((category: CampaignConfigCategoryView) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      title: category.title,
      description: category.description || '',
      icon: category.icon,
      rank: category.rank,
    });
    setFormErrors({});
    setDialogMode('edit');
    setIsDialogOpen(true);
  }, []);

  const validateForm = useCallback(async (): Promise<boolean> => {
    try {
      await campaignConfigCategoryValidationSchema.validate(formData, { abortEarly: false });
      setFormErrors({});
      return true;
    } catch (error) {
      if (error instanceof ValidationError) {
        const errors: Record<string, string> = {};
        error.inner.forEach((err) => {
          if (err.path) {
            errors[err.path] = err.message;
          }
        });
        setFormErrors(errors);
      }
      return false;
    }
  }, [formData]);

  const handleCreate = useCallback(async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      await createConfigCategory({
        name: formData.name,
        title: formData.title,
        description: formData.description || undefined,
        icon: formData.icon,
        rank: formData.rank,
      });
      closeDialog();
    } catch (error) {
      console.error('Error creating config category:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, formData, createConfigCategory, closeDialog]);

  const handleUpdate = useCallback(async () => {
    if (!editingCategory) return;

    const isValid = await validateForm();
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      await updateConfigCategory(editingCategory.name, {
        title: formData.title,
        description: formData.description || undefined,
        icon: formData.icon,
        rank: formData.rank,
        status: editingCategory.status,
      });
      closeDialog();
    } catch (error) {
      console.error('Error updating config category:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [editingCategory, validateForm, formData, updateConfigCategory, closeDialog]);

  const handleActivate = useCallback(async (category: CampaignConfigCategoryView) => {
    try {
      await updateConfigCategory(category.name, {
        title: category.title,
        description: category.description,
        icon: category.icon,
        rank: category.rank,
        status: 'active',
      });
      await reloadConfigCategories();
    } catch (error) {
      console.error('Error activating config category:', error);
    }
  }, [updateConfigCategory, reloadConfigCategories]);

  const handleDelete = useCallback(async (names: string[]) => {
    try {
      await deleteConfigCategories(names);
      await reloadConfigCategories();
    } catch (error) {
      console.error('Error deleting config categories:', error);
    }
  }, [deleteConfigCategories]);

  const updateFormField = useCallback((field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);

  return {
    // State
    isPageLoading: isLoading,
    configCategories,
    filters,
    totalCount,
    error,
    isDialogOpen,
    dialogMode,
    editingCategory,
    formData,
    formErrors,
    isSubmitting,

    // Actions
    changeSearchFilter,
    changeSorting,
    changePagination,
    openCreateDialog,
    closeDialog,
    openEditDialog,
    handleCreate,
    handleUpdate,
    handleActivate,
    handleDelete,
    updateFormField,
  };
};
